import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EvidenceNumber } from '../../types/evidence-numbers'
import { EvidenceRegisterView } from './chain-of-custody/EvidenceRegisterView'
import { EvidenceDetailPanel } from './chain-of-custody/EvidenceDetailPanel'
import { ChainTimelineView } from './chain-of-custody/ChainTimelineView'
import { CustodyActionsTab } from './chain-of-custody/CustodyActionsTab'

interface CaseChainOfCustodyTabProps {
  caseId: string
}

type TabId = 'register' | 'timeline' | 'actions'

export function CaseChainOfCustodyTab({ caseId }: CaseChainOfCustodyTabProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>('register')
  const [selectedEvidence, setSelectedEvidence] = useState<EvidenceNumber | null>(null)

  return (
    <div className="space-y-4">
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      {activeTab === 'register' && (
        <EvidenceRegisterView caseId={caseId} onSelectEvidence={setSelectedEvidence} />
      )}
      {activeTab === 'timeline' && <ChainTimelineView caseId={caseId} />}
      {activeTab === 'actions' && <CustodyActionsTab caseId={caseId} />}
      <EvidenceDetailPanel
        caseId={caseId}
        evidenceNumber={selectedEvidence}
        onClose={() => {
          setSelectedEvidence(null)
        }}
      />
    </div>
  )
}

interface TabBarProps {
  activeTab: TabId
  onTabChange: (tab: TabId) => void
}

function TabBar({ activeTab, onTabChange }: TabBarProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'register', label: t('title') },
    { id: 'timeline', label: t('chainTimeline') },
    { id: 'actions', label: t('custodyActions.tabTitle') },
  ]

  return (
    <div className="flex gap-1 border-b border-[var(--border)]">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => {
            onTabChange(tab.id)
          }}
          className={`px-4 py-2 text-sm font-[var(--font-mono)] font-medium tracking-wider uppercase transition-colors ${
            activeTab === tab.id
              ? 'border-b-2 border-[var(--accent)] bg-[var(--accent-dim)] text-[var(--accent)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
